import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SetLoading } from '../core/loading.state';
import { AuthService } from './auth.service';
import { catchError, delay, EMPTY, finalize, first, switchMap, tap } from 'rxjs';
import { LoginUserInput, ResetState, Role, SignupUserInput, UserProfileData, UserType } from '../shared';
import { RouteToLogin } from './routing.state';
import { ToastService } from '../core/toast.service';
import { AppFeaturesState } from './app-feature.state';
import { FirebaseState } from '../core/firebase.state';

export class AuthPatchState {
	static readonly type = '@authState.patchState';
	constructor(public state: Partial<AuthStateModel>) {}
}

export class AuthLoginUser {
	static readonly type = '@authState.loginUser';
	constructor(public loginUserInput: LoginUserInput) {}
}

export class AuthLoginUserAs {
	static readonly type = '@authState.loginUserAs';
	constructor(public role: UserType) {}
}

export class AuthSignupUser {
	static readonly type = '@authState.signupUser';
	constructor(public signupUserInput: SignupUserInput, public reCaptchaToken: string) {}
}

export class AuthRefreshToken {
	static readonly type = '@authState.authRefreshToken';
}

export class AuthWithToken {
	static readonly type = '@authState.authWithToken';
}

export class AuthResendVerifyEmail {
	static readonly type = '@authState.resendVerifyEmail';
}

export class AuthChangeVerifyEmail {
	static readonly type = '@authState.changeVerifyEmail';
	constructor(public email: string, public confirmEmail: string) {}
}

export class AuthLogOutUser {
	static readonly type = '@authState.logOutUser';
}

export class AuthUpdateUserProfile {
	static readonly type = '@authState.updateUserProfile';
	constructor(public userData: Partial<UserProfileData>) {}
}

export class AuthUpdateUserProfilePhoto {
	static readonly type = '@authState.updateUserProfilePhoto';
	constructor(public photo: File, public offsetX: number, public offsetY: number) {}
}

export class ResetMediaState {
	static readonly type = '@mediaState.resetState';
}

interface AuthStateModel {
	token?: string;
	email?: string;
	verified?: boolean;
	userType?: UserType;
	usersId?: string;
	studiosId?: string;
	guardiansId?: string;
	usersTypeId?: string;
	competitionsId?: string;
	userFname?: string;
	userLname?: string;
	userPhone?: string;
	roles?: Role[];
	message?: string;
	currentRole?: Role;
	dateVerified?: string;
}

@State<AuthStateModel>({
	name: 'AuthState',
	defaults: {
		userFname: undefined,
		userLname: undefined,
		userPhone: undefined,
		token: undefined,
		email: undefined,
		verified: undefined,
		userType: undefined,
		usersId: undefined,
		studiosId: undefined,
		guardiansId: undefined,
		usersTypeId: undefined,
		competitionsId: undefined,
		roles: undefined,
		message: undefined,
		currentRole: undefined,
		dateVerified: undefined,
	},
})
@Injectable()
export class AuthState {
	constructor(
		private authService: AuthService,
		private appFeaturesState: AppFeaturesState,
		private store: Store,
		private toastService: ToastService,
		private firebaseState: FirebaseState
	) {}

	get usersId() {
		return this.store.selectSnapshot(AuthState.usersId);
	}

	get usersTypeId() {
		return this.store.selectSnapshot(AuthState.usersTypeId);
	}

	get token() {
		return this.store.selectSnapshot(AuthState.token);
	}

	get dateVerified() {
		return this.store.selectSnapshot(AuthState.dateVerified);
	}

	@Selector()
	public static state(state: AuthStateModel) {
		return state;
	}

	@Selector()
	public static token(state: AuthStateModel) {
		return state.token;
	}

	@Selector()
	public static guardiansId(state: AuthStateModel) {
		return state.guardiansId;
	}

	@Selector()
	public static usersId(state: AuthStateModel) {
		return state.usersId;
	}

	@Selector()
	public static usersTypeId(state: AuthStateModel) {
		return state.usersTypeId;
	}

	@Selector()
	public static email(state: AuthStateModel) {
		return state.email;
	}

	@Selector()
	public static userType(state: AuthStateModel) {
		return state.userType;
	}

	@Selector()
	public static roles(state: AuthStateModel) {
		return state.roles;
	}

	@Selector()
	public static message(state: AuthStateModel) {
		return state.message;
	}

	@Selector()
	public static userFname(state: AuthStateModel) {
		return state.userFname;
	}

	@Selector()
	public static userLname(state: AuthStateModel) {
		return state.userLname;
	}

	@Selector()
	public static userPhone(state: AuthStateModel) {
		return state.userPhone;
	}

	@Selector()
	public static currentRole(state: AuthStateModel) {
		return state.currentRole;
	}

	@Selector()
	public static dateVerified(state: AuthStateModel) {
		return state.dateVerified;
	}

	@Selector()
	public static userFullName(state: AuthStateModel) {
		return state.userFname + ' ' + state.userLname;
	}

	@Action(AuthPatchState)
	avatarChange(ctx: StateContext<AuthStateModel>, action: AuthPatchState) {
		ctx.patchState(action.state);
	}

	@Action(AuthLoginUser)
	loginUser(ctx: StateContext<AuthStateModel>, action: AuthLoginUser) {
		ctx.dispatch(new SetLoading(true, this, true, this.loginUser));
		const deviceId = this.appFeaturesState.deviceId;
		const deviceToken = this.firebaseState.deviceToken;
		return this.authService.login(action.loginUserInput.username, action.loginUserInput.password, deviceToken, deviceId).pipe(
			tap((data) => {
				const available: Role[] | undefined =
					data.availableAccounts && Object.keys(data.availableAccounts).length > 0
						? Object.entries(data.availableAccounts).map(([, value]) => value)
						: undefined;
				const current: Role | undefined = Object.entries(data.availableAccounts ?? [])
					.filter(([key]) => key === data.userType)
					.map(([, value]) => value)
					.pop();

				ctx.patchState({
					message: data.message,
					token: data.token,
					email: data.email,
					verified: data.verified,
					userType: data.userType,
					usersId: data.usersId,
					studiosId: data.studiosId,
					guardiansId: data.guardiansId,
					competitionsId: data.competitionsId,
					usersTypeId: data.userTypeId,
					userFname: data.userFname,
					userLname: data.userLname,
					userPhone: data.userPhone,
					roles: available,
					currentRole: current,
				});
			}),
			catchError((err) => {
				this.toastService.showToast(this.appFeaturesState.isApp, {
					message: err.message || err,
				});
				return EMPTY;
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.loginUser)))
		);
	}

	@Action(AuthLoginUserAs)
	loginUserAs(ctx: StateContext<AuthStateModel>, action: AuthLoginUserAs) {
		ctx.dispatch(new SetLoading(true, this, true, this.loginUser));
		return this.authService.loginAs(action.role).pipe(
			tap((data) => {
				if (data.userType === undefined) throw Error('Failed to get user type from backend');

				const { roles } = ctx.getState();
				const current: Role | undefined = roles?.filter((value) => value.key === data.userType).pop();

				ctx.patchState({
					token: data.token,
					email: data.email,
					verified: data.verified,
					userType: data.userType,
					usersId: data.usersId,
					studiosId: data.studiosId,
					guardiansId: data.guardiansId,
					competitionsId: data.competitionsId,
					usersTypeId: data.userTypeId,
					userFname: data.userFname,
					userLname: data.userLname,
					userPhone: data.userPhone,
					currentRole: current,
				});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.loginUser)))
		);
	}

	@Action(AuthWithToken)
	authWithToken(ctx: StateContext<AuthStateModel>) {
		ctx.dispatch(new SetLoading(true, this, true, this.authWithToken));
		return this.authService.checkToken().pipe(
			tap((data) => {
				const available: Role[] | undefined =
					data.availableAccounts && Object.keys(data.availableAccounts).length > 0
						? Object.entries(data.availableAccounts).map(([, value]) => value)
						: undefined;
				const current: Role | undefined = Object.entries(data.availableAccounts ?? [])
					.filter(([key]) => key === data.userType)
					.map(([, value]) => value)
					.pop();

				ctx.patchState({
					message: data.message,
					token: data.token,
					email: data.email,
					verified: data.verified,
					userType: data.userType,
					usersId: data.usersId,
					studiosId: data.studiosId,
					usersTypeId: data.usersTypeId,
					guardiansId: data.guardiansId,
					competitionsId: data.competitionsId,
					userFname: data.userFname,
					userLname: data.userLname,
					userPhone: data.userPhone,
					roles: available,
					currentRole: current,
					dateVerified: data.dateVerified,
				});
			}),
			catchError((err) => {
				throw err;
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.authWithToken)))
		);
	}

	@Action(AuthSignupUser)
	signupUser(ctx: StateContext<AuthStateModel>, action: AuthSignupUser) {
		ctx.dispatch(new SetLoading(true, this, true, this.signupUser));
		return this.authService.signup(action.signupUserInput, action.reCaptchaToken).pipe(
			tap((data) => {
				ctx.patchState({
					message: data.message,
					token: data.token,
					email: data.email,
					verified: data.verified,
					userType: data.userType,
					usersId: data.usersId,
					studiosId: data.studiosId,
					guardiansId: data.guardiansId,
					competitionsId: data.competitionsId,
					usersTypeId: data.userTypeId,
					userFname: data.userFname,
					userLname: data.userLname,
					userPhone: data.userPhone,
				});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.signupUser)))
		);
	}

	@Action(AuthRefreshToken)
	authRefreshToken(ctx: StateContext<AuthStateModel>) {
		ctx.dispatch(new SetLoading(true, this, true, this.authRefreshToken));
		return this.authService.refreshToken().pipe(
			tap((data) => {
				console.log('refresh token data', data);
				// ctx.patchState({
				// 	message: data.message,
				// 	token: data.token,
				// 	email: data.email,
				// 	verified: data.verified,
				// 	userType: data.userType,
				// 	usersId: data.usersId,
				// 	studiosId: data.studiosId,
				// 	guardiansId: data.guardiansId,
				// 	competitionsId: data.competitionsId,
				// 	userFname: data.userFname,
				// 	userLname: data.userLname,
				// 	userPhone: data.userPhone,
				// });
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.authRefreshToken)))
		);
	}

	@Action(AuthChangeVerifyEmail)
	changeVerifyEmail(ctx: StateContext<AuthStateModel>, action: AuthChangeVerifyEmail) {
		const { usersId } = ctx.getState();

		if (!usersId) throw Error('User ID is undefined');

		ctx.dispatch(new SetLoading(true, this, true, this.changeVerifyEmail));
		return this.authService.changeVerificationEmail(usersId, action.email, action.confirmEmail).pipe(
			tap((data) => {
				this.toastService.showToast(this.appFeaturesState.isApp, {
					message: data.message,
					color: 'success',
				});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.changeVerifyEmail)))
		);
	}

	@Action(AuthResendVerifyEmail)
	resendEmailVerificationCode(ctx: StateContext<AuthStateModel>) {
		const { usersId } = ctx.getState();

		if (!usersId) throw Error('User ID is undefined');

		ctx.dispatch(new SetLoading(true, this, true, this.resendEmailVerificationCode));
		return this.authService.resendEmailVerificationCode(usersId).pipe(
			tap((data) => {
				this.toastService.showToast(this.appFeaturesState.isApp, {
					message: data.message,
					color: 'success',
				});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.resendEmailVerificationCode)))
		);
	}

	@Action(AuthUpdateUserProfile)
	updateUserProfile(ctx: StateContext<AuthStateModel>, action: AuthUpdateUserProfile) {
		const { usersId } = ctx.getState();

		if (!usersId) throw Error('User ID is undefined');

		ctx.dispatch(new SetLoading(true, this, true, this.updateUserProfile));
		return this.authService.updateUserProfile(usersId, action.userData).pipe(
			tap((data) => {
				this.toastService.showToast(this.appFeaturesState.isApp, {
					message: data.message,
					color: 'success',
				});

				ctx.patchState({});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.updateUserProfile)))
		);
	}

	@Action(AuthUpdateUserProfilePhoto)
	updateUserProfilePhoto(ctx: StateContext<AuthStateModel>, action: AuthUpdateUserProfilePhoto) {
		const { guardiansId } = ctx.getState();

		if (!guardiansId) throw Error('Guardian ID is undefined');

		ctx.dispatch(new SetLoading(true, this, true, this.updateUserProfilePhoto));
		return this.authService.updateUserProfilePhoto(guardiansId, action.photo, action.offsetX, action.offsetY).pipe(
			tap((data) => {
				this.toastService.showToast(this.appFeaturesState.isApp, {
					message: data.message,
					color: 'success',
				});
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.updateUserProfilePhoto)))
		);
	}

	@Action(AuthLogOutUser)
	logOutUser(ctx: StateContext<AuthStateModel>) {
		const deviceId = this.appFeaturesState.deviceId;

		ctx.dispatch(new SetLoading(true, this, true, this.logOutUser));
		return this.authService.logoutUser(deviceId).pipe(
			switchMap(() =>
				ctx.dispatch(new RouteToLogin()).pipe(
					first(),
					delay(1000),
					switchMap(() => ctx.dispatch(new ResetState()))
				)
			),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.logOutUser)))
		);
	}

	@Action(ResetState)
	resetState(ctx: StateContext<AuthStateModel>) {
		ctx.setState({
			userFname: undefined,
			userLname: undefined,
			userPhone: undefined,
			token: undefined,
			email: undefined,
			verified: undefined,
			userType: undefined,
			usersId: undefined,
			studiosId: undefined,
			guardiansId: undefined,
			usersTypeId: undefined,
			competitionsId: undefined,
			roles: undefined,
			message: undefined,
			currentRole: undefined,
			dateVerified: undefined,
		});
	}
}
